<template>
  <CustomModal id="config-criteria-modal"
               class="basic-modal"
               v-bind:title="$t('evaluations.modal.config_criteria.title')"
               v-bind:ok-title="$t('evaluations.modal.config_criteria.ok_title')"
               v-bind:close-title="$t('common.cancel')"
               v-on:ok="saveCriteria()"
               v-on:cancel="$emit('cancel')">

    <div class="class-subject-container">
      <div class="typography--body-3 typography--color-secondary typography--font-weight-bold pr-8">
        {{ selectedClassName }}
      </div>
      <div class="typography--body-3 typography--color-secondary typography--font-weight-bold pl-8">
        {{ subjectMap[selectedSubjectId] }}
      </div>
    </div>

    <div class="evluation-criteria-container mt-27 pt-20 pb-20" v-if="loading">
      <LoadingBounce></LoadingBounce>
    </div>

    <div class="evluation-criteria-container" v-else>

      <div class="score-description-box">
        <div class="score-description">
          <div class="score-mark high-score-mark">
            {{ $t('evaluations.modal.config_criteria.body.high_score_mark') }}
          </div>

          <div class="score-description-text" v-html="$t('evaluations.modal.config_criteria.body.high_score', { score: criteriaValue })"></div>
        </div>

        <div class="score-description">
          <div class="score-mark middle-score-mark">
            {{ $t('evaluations.modal.config_criteria.body.middle_score_mark') }}
          </div>

          <div class="score-description-text" v-html="$t('evaluations.modal.config_criteria.body.middle_score', { score: criteriaValue })"></div>          
        </div>
      </div>

      <div class="score-control-box">
        <vue-slider v-model="criteriaValue"
                    v-bind:interval="5"
                    v-bind:tooltip="false"
                    v-bind:piecewise="true"
                    v-bind:height="12"
                    v-bind:bg-style="{ 'background-color': '#fa8471' }"
                    v-bind:process-style="{ 'background-color': '#00c896' }"
                    v-bind:piecewise-style="{ 'background-color': '#fff', width: '1px', 'border-radius': 'initial' }">
        </vue-slider>

        <div class="typography--body-3 typography--color-disabled typography--font-weight-500 float-left">
          {{ $t('evaluations.modal.config_criteria.body.min_value') }}
        </div>

        <div class="typography--body-3 typography--color-disabled typography--font-weight-500 float-right">
          {{ $t('evaluations.modal.config_criteria.body.max_value') }}
        </div>

        <div class="typography--body-3 typography--color-secondary text-center score-control-help">
          {{ $t('evaluations.modal.config_criteria.body.control_help') }}
        </div>
      </div>
    </div>
  </CustomModal>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { cloneDeep } from 'lodash';
  import vueSlider from 'vue-slider-component';

  export default {
    name: 'evaluation-criteria-modal',

    components: {
      vueSlider,
    },

    data() {
      return {
        loading: true,

        criteriaValue: 60,
      };
    },

    computed: {
      ...mapGetters({
        evaluationCriteria: 'evaluationCriteria',
        evaluationFilters: 'evaluationFilters',
        managingClasses: 'managingClasses',
        subjectMap: 'evaluationSubjectMap',
      }),

      selectedSubjectId() {
        return this.evaluationFilters.subject_id;
      },

      selectedClassId() {
        return this.evaluationFilters.class_id;
      },

      selectedClassName() {
        if (this.managingClasses[this.selectedClassId]) {
          const selectedClass = this.managingClasses[this.selectedClassId];

          return `${selectedClass.year} ${selectedClass.grade} - ${selectedClass.name}`;
        }

        return '';
      },
    },

    methods: {
      ...mapActions([
        'fetchEvaluationCriteria',
        'saveEvaluationCriteria',
      ]),

      fetchCriteria() {
        const params = {
          classId: this.selectedClassId,
          filters: {
            subject_id: this.selectedSubjectId,
          },
        };

        this.fetchEvaluationCriteria(params)
          .then(() => {
            this.criteriaValue = cloneDeep(this.evaluationCriteria);
            this.loading = false;
          });
      },

      saveCriteria() {
        this.loading = true;

        const params = {
          classId: this.selectedClassId,
          criteriaBody: {
            subject_id: this.selectedSubjectId,
            criteria: [this.criteriaValue, 0],
          },
        };

        this.saveEvaluationCriteria(params)
          .then(() => (
            this.$emit('criteria-updated')
          ));
      },
    },

    created() {
      this.fetchCriteria();
    },

    beforeDestroy() {
      this.loading = true;
    },
  };
</script>

<style lang="scss">
  #config-criteria-modal {
    .modal-dialog {
      width: 580px;
    }

    .class-subject-container {
      line-height: 18px;
      margin-bottom: 28px;

      div {
        position: relative;
        display: inline-block;

        &:first-child:after {
          position: absolute;
          content: '';
          top: 3px;
          right: -3px;
          width: 2px;
          height: 12px;
          border: solid 1px rgba(0, 0, 0, 0.12);
        }
      }
    }

    .evluation-criteria-container {
      .score-description-box {
        padding: 20px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 2px;
        margin-bottom: 8px;

        .score-description:first-child {
          margin-bottom: 16px;
        }

        .score-mark {
          display: inline-block;
          font-size: 14px;
          font-weight: 500;
          line-height: 23px;
          text-align: center;
          width: 24px;
          height: 24px;
          border: 1px solid;
          border-radius: 50%;
          margin-right: 4px;

          &.high-score-mark {
            border-color: #fa8471;
            color: #fa8471;
          }

          &.middle-score-mark {
            border-color: #00c896;
            color: #00c896;
          }
        }

        .score-description-text {
          display: inline-block;
          line-height: 23px;
        }
      }

      .score-control-box {
        position: relative;
        padding-top: 18px;

        .vue-slider-piecewise {
          .vue-slider-piecewise-item:nth-child(even) {
            display: none;
          }
        }

        .score-control-help {
          margin-top: 30px;
          margin-bottom: 22px;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/evaluations/components/EvaluationCriteriaModal.vue